import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Beef, LogOut, Home, Save, Upload, Eye, Palette, Type, Layout, Image, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUploader from '@/components/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});


const CARD_SECTION_FIELDS = {
  colors: ['card_bg_color', 'card_bg_gradient_end', 'card_accent_color', 'card_text_color'],
  images: ['card_logo_url', 'card_background_image'],
  layout: ['card_logo_position', 'card_points_position', 'card_show_total_points', 'card_show_qr_preview'],
  text: ['business_name', 'card_expiry_message']
};

const CARD_SECTION_LABELS = {
  colors: 'Colori',
  images: 'Immagini',
  layout: 'Layout',
  text: 'Testi'
};

const defaultSettings = {
  business_name: 'Macelleria Borgese',
  card_bg_color: '#5D1822',
  card_bg_gradient_end: '#3A0F15',
  card_accent_color: '#C5A059',
  card_text_color: '#FFFFFF',
  card_logo_url: '',
  card_background_image: '',
  card_logo_position: 'top-right',
  card_points_position: 'bottom-left',
  card_show_total_points: true,
  card_expiry_message: 'I punti scadono ogni fine mese',
  card_show_qr_preview: true
};

export default function CardEditor({ user, onLogout }) {
  const [settings, setSettings] = useState(defaultSettings);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('colors');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.log('Using default settings');
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const fields = CARD_SECTION_FIELDS[activeSection] || [];
    const payload = Object.fromEntries(fields.map(field => [field, settings[field]]));
    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/settings`, payload, getAuthHeaders());
      setSettings(prev => ({ ...prev, ...(response.data?.settings || {}) }));
      toast.success(`${CARD_SECTION_LABELS[activeSection]} della card salvato correttamente!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const label = CARD_SECTION_LABELS[activeSection];
    if (!window.confirm(`Ripristinare solo "${label}" ai valori predefiniti?`)) return;
    const fields = CARD_SECTION_FIELDS[activeSection] || [];
    setSettings(prev => {
      const next = { ...prev };
      fields.forEach(field => { next[field] = defaultSettings[field]; });
      return next;
    });
    toast.info(`${label} ripristinato nell'editor. Premi Salva per confermare.`);
  };

  // Get position classes for logo
  const getLogoPositionClasses = () => {
    switch (settings.card_logo_position) {
      case 'top-left': return 'justify-start';
      case 'top-center': return 'justify-center';
      case 'top-right': return 'justify-end';
      default: return 'justify-end';
    }
  };

  // Get position classes for points
  const getPointsPositionClasses = () => {
    switch (settings.card_points_position) {
      case 'bottom-left': return 'justify-start text-left';
      case 'bottom-center': return 'justify-center text-center';
      case 'bottom-right': return 'justify-end text-right';
      default: return 'justify-start text-left';
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="card-editor">
      {/* Header */}
      <header className="bg-[#5D1822] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <Home size={20} />
              <span className="hidden sm:inline text-sm">Dashboard</span>
            </Link>
            <div className="h-6 w-px bg-white/20"></div>
            <div className="flex items-center gap-3">
              <Beef className="w-8 h-8 text-[#C5A059]" />
              <div>
                <h1 className="text-xl font-serif font-bold">Editor Fidelity Card</h1>
                <p className="text-xs text-[#C5A059]">Personalizza la card</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              className="text-white/80 hover:text-white flex items-center gap-2 text-sm"
            >
              <RotateCcw size={18} />
              <span className="hidden sm:inline">Reset</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#C5A059] text-[#2C2C2C] px-4 py-2 rounded-full font-semibold hover:bg-[#E5C585] transition-colors flex items-center gap-2"
            >
              <Save size={18} />
              {saving ? 'Salvataggio...' : `Salva ${CARD_SECTION_LABELS[activeSection]}`}
            </button>
            <button onClick={onLogout} className="text-white/80 hover:text-white ml-2">
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Preview Section */}
          <div className="order-2 lg:order-1">
            <div className="sticky top-24">
              <h2 className="text-xl font-serif font-bold text-[#5D1822] mb-4 flex items-center gap-2">
                <Eye size={24} />
                Anteprima Card
              </h2>
              
              {/* Card Preview */}
              <div 
                className="rounded-2xl p-8 text-white card-shadow relative overflow-hidden"
                style={{
                  background: settings.card_background_image 
                    ? `linear-gradient(135deg, ${settings.card_bg_color}dd, ${settings.card_bg_gradient_end}dd), url(${settings.card_background_image})`
                    : `linear-gradient(135deg, ${settings.card_bg_color}, ${settings.card_bg_gradient_end})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  color: settings.card_text_color
                }}
              >
                {/* Decorative circle */}
                <div 
                  className="absolute top-0 right-0 w-64 h-64 rounded-full -translate-y-1/2 translate-x-1/2 opacity-10"
                  style={{ backgroundColor: settings.card_accent_color }}
                ></div>
                
                <div className="relative z-10">
                  {/* Header Row - Logo and Name */}
                  <div className={`flex items-start mb-8 ${getLogoPositionClasses()}`}>
                    <div className={`flex items-center gap-3 ${settings.card_logo_position === 'top-center' ? 'flex-col' : ''}`}>
                      {settings.card_logo_url ? (
                        <img 
                          src={settings.card_logo_url} 
                          alt="Logo" 
                          className="w-12 h-12 object-contain rounded-lg bg-white/10 p-1"
                        />
                      ) : (
                        <Beef className="w-12 h-12" style={{ color: settings.card_accent_color }} />
                      )}
                      <div className={settings.card_logo_position === 'top-center' ? 'text-center' : ''}>
                        <p className="text-sm mb-1" style={{ color: settings.card_accent_color }}>Fidelity Card</p>
                        <h3 className="text-xl font-serif font-bold">{settings.business_name}</h3>
                      </div>
                    </div>
                    
                    {/* QR Preview */}
                    {settings.card_show_qr_preview && settings.card_logo_position !== 'top-right' && (
                      <div className="bg-white p-2 rounded-lg ml-auto">
                        <div className="w-12 h-12 bg-gray-200 flex items-center justify-center text-gray-400 text-xs">QR</div>
                      </div>
                    )}
                  </div>
                  
                  {/* Points Row */}
                  <div className={`flex items-end ${getPointsPositionClasses()}`}>
                    <div className={settings.card_points_position === 'bottom-center' ? 'w-full' : ''}>
                      <p className="text-sm mb-2 opacity-70">Punti Attuali</p>
                      <p className="text-5xl font-mono font-bold" style={{ color: settings.card_accent_color }}>42</p>
                    </div>
                    
                    {settings.card_show_total_points && settings.card_points_position !== 'bottom-right' && (
                      <div className={`${settings.card_points_position === 'bottom-center' ? 'ml-8' : 'ml-auto'} text-right`}>
                        <p className="text-sm mb-2 opacity-70">Totale</p>
                        <p className="text-2xl font-mono font-bold">156</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Expiry Message */}
                  {settings.card_expiry_message && (
                    <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                      <p className="text-sm flex items-center gap-2">
                        <svg className="w-4 h-4" style={{ color: settings.card_accent_color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{settings.card_expiry_message}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <p className="text-sm text-[#5A5A5A] mt-4 text-center">
                Questa è un'anteprima. I clienti vedranno la card con i loro dati reali.
              </p>
            </div>
          </div>

          {/* Editor Section */}
          <div className="order-1 lg:order-2 space-y-6">
            {/* Section Tabs */}
            <div className="flex gap-2 flex-wrap">
              {[
                { id: 'colors', icon: Palette, label: 'Colori' },
                { id: 'images', icon: Image, label: 'Immagini' },
                { id: 'layout', icon: Layout, label: 'Layout' },
                { id: 'text', icon: Type, label: 'Testi' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveSection(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-colors ${
                    activeSection === tab.id
                      ? 'bg-[#5D1822] text-white'
                      : 'bg-white text-[#5D1822] hover:bg-[#5D1822]/10'
                  }`}
                >
                  <tab.icon size={18} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Colors Section */}
            {activeSection === 'colors' && (
              <div className="bg-white rounded-xl p-6 card-shadow space-y-6">
                <h3 className="text-lg font-semibold text-[#5D1822] flex items-center gap-2">
                  <Palette size={20} />
                  Colori Card
                </h3>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                      Colore Sfondo (Inizio)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={settings.card_bg_color}
                        onChange={(e) => handleChange('card_bg_color', e.target.value)}
                        className="h-12 w-16 rounded-lg border border-[#E5E5E5] cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.card_bg_color}
                        onChange={(e) => handleChange('card_bg_color', e.target.value)}
                        className="input-default flex-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                      Colore Sfondo (Fine Gradiente)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={settings.card_bg_gradient_end}
                        onChange={(e) => handleChange('card_bg_gradient_end', e.target.value)}
                        className="h-12 w-16 rounded-lg border border-[#E5E5E5] cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.card_bg_gradient_end}
                        onChange={(e) => handleChange('card_bg_gradient_end', e.target.value)}
                        className="input-default flex-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                      Colore Accento (Punti, Icone)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={settings.card_accent_color}
                        onChange={(e) => handleChange('card_accent_color', e.target.value)}
                        className="h-12 w-16 rounded-lg border border-[#E5E5E5] cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.card_accent_color}
                        onChange={(e) => handleChange('card_accent_color', e.target.value)}
                        className="input-default flex-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                      Colore Testo
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={settings.card_text_color}
                        onChange={(e) => handleChange('card_text_color', e.target.value)}
                        className="h-12 w-16 rounded-lg border border-[#E5E5E5] cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.card_text_color}
                        onChange={(e) => handleChange('card_text_color', e.target.value)}
                        className="input-default flex-1"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Color Presets */}
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Preset Colori
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { name: 'Borgese', bg: '#5D1822', end: '#3A0F15', accent: '#C5A059' },
                      { name: 'Elegante', bg: '#1a1a2e', end: '#16213e', accent: '#e94560' },
                      { name: 'Verde', bg: '#2d5a27', end: '#1e3d19', accent: '#a4c639' },
                      { name: 'Blu', bg: '#1e3a5f', end: '#152238', accent: '#64b5f6' },
                      { name: 'Oro', bg: '#8B7355', end: '#5D4E37', accent: '#FFD700' }
                    ].map(preset => (
                      <button
                        key={preset.name}
                        onClick={() => {
                          handleChange('card_bg_color', preset.bg);
                          handleChange('card_bg_gradient_end', preset.end);
                          handleChange('card_accent_color', preset.accent);
                        }}
                        className="px-3 py-2 rounded-lg border-2 border-[#E5E5E5] hover:border-[#5D1822] transition-colors text-sm flex items-center gap-2"
                      >
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ background: `linear-gradient(135deg, ${preset.bg}, ${preset.end})` }}
                        ></div>
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Images Section */}
            {activeSection === 'images' && (
              <div className="bg-white rounded-xl p-6 card-shadow space-y-6">
                <h3 className="text-lg font-semibold text-[#5D1822] flex items-center gap-2">
                  <Image size={20} />
                  Immagini
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    URL Logo (opzionale)
                  </label>
                  <input
                    type="url"
                    value={settings.card_logo_url || ''}
                    onChange={(e) => handleChange('card_logo_url', e.target.value)}
                    className="input-default w-full"
                    placeholder="https://esempio.com/logo.png"
                  />
                  <p className="text-xs text-[#5A5A5A] mt-1">
                    Inserisci l'URL di un'immagine per il logo. Lascia vuoto per l'icona predefinita.
                  </p>
                </div>
                
                <ImageUploader
                  label="Immagine Sfondo Card (opzionale)"
                  currentImage={settings.card_background_image}
                  onImageChange={(url) => handleChange('card_background_image', url)}
                  isDark={false}
                />
                
                {/* Sample Background Images */}
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Oppure scegli uno sfondo suggerito
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      'https://images.pexels.com/photos/1639557/pexels-photo-1639557.jpeg?auto=compress&cs=tinysrgb&w=400',
                      'https://images.pexels.com/photos/618775/pexels-photo-618775.jpeg?auto=compress&cs=tinysrgb&w=400',
                      'https://images.pexels.com/photos/1251208/pexels-photo-1251208.jpeg?auto=compress&cs=tinysrgb&w=400',
                      'https://images.pexels.com/photos/3535383/pexels-photo-3535383.jpeg?auto=compress&cs=tinysrgb&w=400',
                      'https://images.pexels.com/photos/65175/pexels-photo-65175.jpeg?auto=compress&cs=tinysrgb&w=400',
                      ''
                    ].map((url, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleChange('card_background_image', url)}
                        className={`aspect-video rounded-lg overflow-hidden border-2 transition-colors ${
                          settings.card_background_image === url ? 'border-[#5D1822]' : 'border-[#E5E5E5]'
                        }`}
                      >
                        {url ? (
                          <img src={url} alt={`Sfondo ${idx + 1}`} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center text-white text-xs">
                            Nessuno
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Layout Section */}
            {activeSection === 'layout' && (
              <div className="bg-white rounded-xl p-6 card-shadow space-y-6">
                <h3 className="text-lg font-semibold text-[#5D1822] flex items-center gap-2">
                  <Layout size={20} />
                  Layout e Posizioni
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Posizione Logo
                  </label>
                  <div className="flex gap-2">
                    {[
                      { value: 'top-left', label: 'Sinistra' },
                      { value: 'top-center', label: 'Centro' },
                      { value: 'top-right', label: 'Destra' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => handleChange('card_logo_position', opt.value)}
                        className={`flex-1 py-3 rounded-lg border-2 font-medium transition-colors ${
                          settings.card_logo_position === opt.value
                            ? 'border-[#5D1822] bg-[#5D1822] text-white'
                            : 'border-[#E5E5E5] hover:border-[#5D1822]'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Posizione Punti
                  </label>
                  <div className="flex gap-2">
                    {[
                      { value: 'bottom-left', label: 'Sinistra' },
                      { value: 'bottom-center', label: 'Centro' },
                      { value: 'bottom-right', label: 'Destra' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => handleChange('card_points_position', opt.value)}
                        className={`flex-1 py-3 rounded-lg border-2 font-medium transition-colors ${
                          settings.card_points_position === opt.value
                            ? 'border-[#5D1822] bg-[#5D1822] text-white'
                            : 'border-[#E5E5E5] hover:border-[#5D1822]'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.card_show_total_points}
                      onChange={(e) => handleChange('card_show_total_points', e.target.checked)}
                      className="w-5 h-5 rounded border-[#E5E5E5] text-[#5D1822] focus:ring-[#5D1822]"
                    />
                    <span className="text-sm text-[#2C2C2C]">Mostra "Totale Punti Guadagnati"</span>
                  </label>
                  
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.card_show_qr_preview}
                      onChange={(e) => handleChange('card_show_qr_preview', e.target.checked)}
                      className="w-5 h-5 rounded border-[#E5E5E5] text-[#5D1822] focus:ring-[#5D1822]"
                    />
                    <span className="text-sm text-[#2C2C2C]">Mostra anteprima QR Code sulla card</span>
                  </label>
                </div>
              </div>
            )}

            {/* Text Section */}
            {activeSection === 'text' && (
              <div className="bg-white rounded-xl p-6 card-shadow space-y-6">
                <h3 className="text-lg font-semibold text-[#5D1822] flex items-center gap-2">
                  <Type size={20} />
                  Testi
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Nome Attività sulla Card
                  </label>
                  <input
                    type="text"
                    value={settings.business_name}
                    onChange={(e) => handleChange('business_name', e.target.value)}
                    className="input-default w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Messaggio Scadenza Punti
                  </label>
                  <input
                    type="text"
                    value={settings.card_expiry_message}
                    onChange={(e) => handleChange('card_expiry_message', e.target.value)}
                    className="input-default w-full"
                    placeholder="I punti scadono ogni fine mese"
                  />
                  <p className="text-xs text-[#5A5A5A] mt-1">
                    Lascia vuoto per nascondere il messaggio.
                  </p>
                </div>
              </div>
            )}

            {/* Save Button (Mobile) */}
            <div className="lg:hidden">
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                <Save size={20} />
                {saving ? 'Salvataggio...' : 'Salva Modifiche'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
